import { useEffect, type ReactNode } from 'react';
import { BrowserRouter } from 'react-router-dom';

import { AppProvider } from '../app/provider/AppContext';
import { EventProvider } from '../app/provider/EventContext';
import { ToastProvider } from '../app/provider/ToastContext';
import { useSyncToStorage } from '../shared/hooks/useSyncToStorage';
import { STORAGE_KEYS } from '../shared/utils/storage';


import AppRoutes from './Routes';
import AppLayout from './layout/AppLayout';

/**
 * Inner shell — rendered inside <AppProvider> so hooks can access context.
 * Handles context ↔ sessionStorage sync.
 */
function AppShell({ children }: { children: ReactNode }) {
  useSyncToStorage();
  return <>{children}</>;
}

function App() {
  useEffect(() => {
    // Clear any stale errors from previous sessions
    sessionStorage.removeItem(STORAGE_KEYS.ERROR);

    // Guest users: unlock cart operations immediately
    const pingToken = sessionStorage.getItem(STORAGE_KEYS.PING_ACCESS_TOKEN)
      || localStorage.getItem(STORAGE_KEYS.PING_ACCESS_TOKEN);
    if (!pingToken) {
      sessionStorage.setItem(STORAGE_KEYS.CART_RESOLVED, 'true');
    }
  }, []);

  return (
    <AppProvider>
      <AppShell>
        <ToastProvider>
          <BrowserRouter>
            <EventProvider>
              <AppLayout>
                <AppRoutes />
              </AppLayout>
            </EventProvider>
          </BrowserRouter>
        </ToastProvider>
      </AppShell>
    </AppProvider>
  );
}

export default App;